import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import { PiDownloadSimple } from 'react-icons/pi';
import { Link } from 'wouter';
import { PayerKycBadge } from '@/components/PayerKycBadge';
import { proformaStateLabel } from '@/lib/proformaState';
import { TSplitPart, useDownloadInvoicePDF } from '@/models/invoices';
import { usePayers } from '@/models/payers';
import {
  type TSplitLine,
  type TSplitPlanItem,
  useIssueInstallment,
} from '@/models/split-plan';
import { scheduleRows } from '@/pages/offers/scheduleRows';
import { InstallmentContents } from './payments/InstallmentContents';

const STATUS_COLOR: Record<string, string> = {
  issued: 'blue',
  paid: 'green',
  cancelled: 'gray',
};

const fmtMoney = (n: number, ccy: string) =>
  `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const fmtRate = (rate: number) => Number(rate.toFixed(6));

/**
 * Each installment's share of the plan: its agreed amount over the plan's total, both in the
 * plan's base currency. Fixed when the plan is approved; no currency change moves it.
 */
export function planShares(
  items: { id: number; amount: number | string | null }[],
) {
  const total = items.reduce((s, i) => s + Number(i.amount ?? 0), 0);
  return new Map(
    items.map((i) => [i.id, total > 0 ? Number(i.amount ?? 0) / total : 0]),
  );
}

const pctText = (share: number) => `${Number((share * 100).toFixed(1))}%`;

export function PartsList({
  parts,
  offerId,
  accountId,
  currencyTotals,
  planItems = [],
  selectedLines = [],
  issuableSeq = null,
  canIssueItem = () => false,
  canReadPayers = true,
  recipientBlockReason,
  baseCurrency,
}: {
  parts: TSplitPart[];
  offerId?: number;
  accountId?: number;
  currencyTotals?: Record<string, { paid: number; outstanding: number }> | null;
  /** The currency the plan's amounts are agreed in; every plan amount is labelled with it. */
  baseCurrency?: string;
  /** Supplied on a split offer so every installment — issued or not — gets a row. */
  planItems?: TSplitPlanItem[];
  selectedLines?: TSplitLine[];
  /** The one installment that may be issued right now, if any. */
  issuableSeq?: number | null;
  canIssueItem?: (planItemId: number) => boolean;
  /**
   * Operations hold no payers:read, so the payer column is dropped for them rather
   * than rendered from a request that would only 403. The offer header already names
   * the paying party, so nothing is actually lost.
   */
  canReadPayers?: boolean;
  recipientBlockReason?: string | null;
}) {
  const { data: payers = [] } = usePayers(accountId, undefined, canReadPayers);

  const payerName = (id: number | null) =>
    id == null
      ? '—'
      : (payers.find((p) => p.id === id)?.displayName ?? `#${id}`);
  const payerOf = (id: number | null) =>
    id == null ? null : (payers.find((p) => p.id === id) ?? null);

  const interactive = offerId != null;

  const downloadPDF = useDownloadInvoicePDF();

  const issue = useIssueInstallment(offerId ?? 0);

  // An installment is the unit; its invoice is a property it may not have yet.
  // Parts no plan item claims (a cancelled original, a manual invoice) still get a
  // row of their own — a schedule that silently drops an invoice is worse than one
  // with an odd row in it.
  // A cancelled installment proforma sits under the installment it billed, which
  // is back on the schedule or already issued again.
  const rows = scheduleRows(planItems, parts);
  const base = (baseCurrency ?? 'EUR').toUpperCase();
  const shares = planShares(planItems);
  const planTotal = planItems.reduce((s, i) => s + Number(i.amount ?? 0), 0);

  // An installment's agreed amount is in the plan's base; what its proforma bills is in the
  // proforma's own currency, at the rate it was billed at.
  const amountCell = (item: TSplitPlanItem | null, part: TSplitPart | null) => {
    const billed =
      part &&
      `billed ${fmtMoney(part.payableAmount ?? part.dueAmount, part.currency)}${
        part.billedRate
          ? ` · 1 ${base} = ${fmtRate(part.billedRate.rate)} ${part.currency}`
          : ''
      }`;
    if (!item) return <Text fz="sm">{billed?.replace(/^billed /, '')}</Text>;
    const frozen =
      !part &&
      item.frozenRates &&
      item.currency.toUpperCase() !== base &&
      item.frozenRates[base] != null
        ? `will be billed in ${item.currency} at 1 ${base} = ${fmtRate(item.frozenRates[base])} ${item.currency}${
            item.frozenRatesAt
              ? ` (fixed ${dayjs(item.frozenRatesAt).format('DD.MM.YYYY')})`
              : ''
          }`
        : null;
    return (
      <Stack gap={0}>
        <Text fz="sm">
          {fmtMoney(Number(item.amount ?? 0), base)}{' '}
          <Text span fz="xs" c="dimmed">
            {pctText(shares.get(item.id) ?? 0)}
          </Text>
        </Text>
        {(billed || frozen) && (
          <Text fz="xs" c="dimmed">
            {billed ?? frozen}
          </Text>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="xs">
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Installment</Table.Th>
            {canReadPayers && <Table.Th>Payer</Table.Th>}
            <Table.Th>Due</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Status</Table.Th>
            {interactive && <Table.Th w={40} />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(({ key, item, part, cancelledOf }) => {
            const isIssuable =
              item != null && item.seq === issuableSeq && !recipientBlockReason;
            return (
              <Fragment key={key}>
                <Table.Tr>
                  <Table.Td>
                    {/* Once an installment has an invoice, its row is the way through
                        to that invoice. Accounting reached the offer first and had no
                        route onward but the invoices list and a search by number. */}
                    <Stack gap={0}>
                      <Text fz="sm" c={cancelledOf ? 'dimmed' : undefined}>
                        {item
                          ? `Installment ${item.seq}`
                          : cancelledOf
                            ? `Installment ${cancelledOf.seq}, cancelled proforma`
                            : part?.partNumber == null
                              ? 'Full'
                              : `Part ${part.partNumber}`}
                      </Text>
                      {part && (
                        <Anchor
                          component={Link}
                          to={`~/invoices/${part.id}`}
                          fz="xs"
                        >
                          {part.invoiceNumber ?? `Invoice #${part.id}`}
                        </Anchor>
                      )}
                      {cancelledOf && (
                        <Text fz="xs" c="dimmed">
                          {cancelledOf.replacedBy
                            ? `Replaced by ${cancelledOf.replacedBy.invoiceNumber ?? `invoice #${cancelledOf.replacedBy.id}`}`
                            : `Installment ${cancelledOf.seq} is back on the schedule as not issued`}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  {canReadPayers && (
                    <Table.Td>
                      {part ? (
                        <Group gap={6} wrap="nowrap">
                          <Text fz="sm">{payerName(part.payerId)}</Text>
                          {part.payerId != null && (
                            <PayerKycBadge payer={payerOf(part.payerId)} />
                          )}
                        </Group>
                      ) : (
                        <Text fz="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>
                    {part?.dueAt ? dayjs(part.dueAt).format('DD.MM.YYYY') : '—'}
                  </Table.Td>
                  <Table.Td>{amountCell(item, part)}</Table.Td>
                  <Table.Td>
                    {part ? (
                      <Badge
                        variant="light"
                        color={STATUS_COLOR[part.status] ?? 'gray'}
                      >
                        {proformaStateLabel(part.status)}
                      </Badge>
                    ) : item?.fulfilled ? (
                      <Badge variant="light" color="green">
                        fulfilled
                      </Badge>
                    ) : (
                      <Badge variant="light" color="gray">
                        {item && item.coveredLines.length > 0
                          ? 'awaiting delivery'
                          : 'awaiting coverage'}
                      </Badge>
                    )}
                  </Table.Td>
                  {interactive && (
                    <Table.Td>
                      {part ? (
                        <Tooltip label="Download invoice">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            loading={
                              downloadPDF.isPending &&
                              downloadPDF.variables === String(part.id)
                            }
                            onClick={() => downloadPDF.mutate(String(part.id))}
                          >
                            <PiDownloadSimple size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        item != null &&
                        canIssueItem(item.id) && (
                          <Tooltip
                            label={
                              recipientBlockReason ??
                              'Previous installment must be fully paid'
                            }
                            disabled={isIssuable}
                            multiline
                            w={260}
                          >
                            <span>
                              <Button
                                size="xs"
                                variant="light"
                                disabled={!isIssuable}
                                loading={
                                  issue.isPending && issue.variables === item.id
                                }
                                onClick={() =>
                                  issue.mutate(item.id, {
                                    onError: (err) =>
                                      notifications.show({
                                        color: 'red',
                                        message: String(err),
                                      }),
                                    onSuccess: () =>
                                      notifications.show({
                                        color: 'teal',
                                        message: `Installment ${item.seq} invoice issued.`,
                                      }),
                                  })
                                }
                              >
                                Issue
                              </Button>
                            </span>
                          </Tooltip>
                        )
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
                {item && (
                  <Table.Tr>
                    <Table.Td />
                    <Table.Td
                      colSpan={(interactive ? 5 : 4) - (canReadPayers ? 0 : 1)}
                      pt={0}
                    >
                      <InstallmentContents
                        coveredLines={item.coveredLines}
                        note={item.note}
                        selectedLines={selectedLines}
                      />
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>

      {planItems.length > 0 ? (
        <Text size="sm">
          Agreed total {fmtMoney(planTotal, base)}
          <Text span size="xs" c="dimmed">
            {' '}
            · before VAT and payment fees, which each proforma adds
          </Text>
        </Text>
      ) : (
        currencyTotals && (
          <Group gap="md">
            {Object.entries(currencyTotals).map(([cur, t]) => (
              <Text key={cur} size="sm">
                {fmtMoney(t.paid, cur)} paid · {fmtMoney(t.outstanding, cur)}{' '}
                outstanding
              </Text>
            ))}
          </Group>
        )
      )}
    </Stack>
  );
}
